import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import topAgenciesSchema from "./schema";

// CodeEditor: Allows editing JSON-like top agencies data with validation and
// formatting
function CodeEditor({ topAgencies, onTopAgenciesChanged }) {
  const [code, setCode] = useState(""); // Current editor content
  const [originalCode, setOriginalCode] = useState(""); // Original content for reset
  const [error, setError] = useState(null); // Validation errors
  const [hasChanges, setHasChanges] = useState(false); // Tracks unsaved changes

  // Sync editor content with incoming topAgencies prop
  useEffect(() => {
    const topAgenciesJson = JSON.stringify(topAgencies, null, 2);
    setCode(topAgenciesJson);
    setOriginalCode(topAgenciesJson);
  }, [topAgencies]);

  // Handle user input in the editor
  const handleCodeChange = (event) => {
    setCode(event.target.value);
    setHasChanges(event.target.value !== originalCode);
  };

  // Validate JSON and notify parent component of changes
  const handleValidate = async () => {
    try {
      const newTopAgencies = JSON.parse(code);
      await topAgenciesSchema.validate(newTopAgencies, { strict: true });
      setError(null);
      onTopAgenciesChanged(newTopAgencies);

      // Format JSON and reset state after successful validation
      const prettyCode = JSON.stringify(newTopAgencies, null, 2);
      setOriginalCode(prettyCode);
      setCode(prettyCode);
      setHasChanges(false);
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
    }
  };

  // Reset editor to the original content
  const handleCancel = () => {
    setCode(originalCode);
    setError(null);
    setHasChanges(false);
  };

  return (
    <div>
      <div>
        {/* Textarea for JSON input */}
        <textarea className="form-control" rows="20" value={code} onChange={handleCodeChange} />
        {/* Error message */}
        {error && <div className="text-danger mt-2">{error}</div>}
        {/* Action buttons for validation and reset */}
        {hasChanges && (
          <div className="mt-3">
            <button type="button" className="btn btn-primary me-2" onClick={handleValidate}>
              Validate &amp; save
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

CodeEditor.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  topAgencies: PropTypes.array.isRequired, // Array of top agencies data
  onTopAgenciesChanged: PropTypes.func.isRequired, // Callback to update parent state
};

export default CodeEditor;
